/**
 * Small browser helpers shared by the decoders and encoders.
 *
 * browser-only, covered by the QA matrix in the doc (IMAGE_EDITOR_V2.md §6) —
 * these touch `createImageBitmap`, `HTMLImageElement` and `canvas.toBlob`, none
 * of which behave like the real thing under jsdom.
 */

/** Number of bytes `detectFormat` sniffs. */
export const HEAD_BYTES = 32;

export function createCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    return canvas;
}

/**
 * Blob -> bytes. Prefers `Blob.arrayBuffer()` and falls back to `FileReader`,
 * which is the only one of the two that Safari before 14 (and jsdom) has.
 */
export function blobToUint8(blob: Blob): Promise<Uint8Array> {
    if (typeof (blob as any).arrayBuffer === 'function') {
        return blob.arrayBuffer().then(buffer => new Uint8Array(buffer));
    }
    return new Promise<Uint8Array>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
        reader.onerror = () => reject(new Error('This file could not be read.'));
        reader.readAsArrayBuffer(blob);
    });
}

/** First `n` bytes of a file, for magic-byte detection. */
export function readHead(file: Blob, n: number = HEAD_BYTES): Promise<Uint8Array> {
    return blobToUint8(file.slice(0, n));
}

export function readBytes(file: Blob): Promise<Uint8Array> {
    return blobToUint8(file);
}

/**
 * Decode a blob the browser understands natively. Prefers `createImageBitmap`
 * (off-main-thread, no DOM node); falls back to an `<img>` + object URL for
 * Safari versions where `createImageBitmap` is missing or refuses the type.
 */
export async function blobToBitmap(blob: Blob): Promise<ImageBitmap | HTMLCanvasElement> {
    if (typeof createImageBitmap === 'function') {
        try {
            return await createImageBitmap(blob);
        } catch {
            // fall through to the <img> path
        }
    }
    return await blobToCanvasViaImage(blob);
}

/** `<img>` + object URL + canvas. The universal fallback path. */
export function blobToCanvasViaImage(blob: Blob): Promise<HTMLCanvasElement> {
    return new Promise<HTMLCanvasElement>((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = createCanvas(img.naturalWidth || img.width, img.naturalHeight || img.height);
                canvas.getContext('2d').drawImage(img, 0, 0);
                resolve(canvas);
            } catch (err) {
                reject(err);
            } finally {
                URL.revokeObjectURL(url);
            }
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('The browser could not decode this image.'));
        };
        img.src = url;
    });
}

export function sourceWidth(source: ImageBitmap | HTMLCanvasElement): number {
    return source.width;
}

export function sourceHeight(source: ImageBitmap | HTMLCanvasElement): number {
    return source.height;
}

/**
 * Alpha probe: draw the source into a canvas of at most 64x64 and look for any
 * sample that is not fully opaque. A downscale is enough — we only need to know
 * whether the "keep transparency" copy and the PNG/WebP defaults apply, and a
 * full-size `getImageData` on a 60 MB TIFF is exactly the memory spike §6 warns
 * about.
 */
export function detectAlpha(source: ImageBitmap | HTMLCanvasElement): boolean {
    const w = sourceWidth(source);
    const h = sourceHeight(source);
    if (!w || !h) { return false; }
    const scale = Math.min(1, 64 / Math.max(w, h));
    const probe = createCanvas(Math.max(1, Math.round(w * scale)), Math.max(1, Math.round(h * scale)));
    const ctx = probe.getContext('2d', { willReadFrequently: true } as any) as CanvasRenderingContext2D;
    if (!ctx) { return false; }
    ctx.clearRect(0, 0, probe.width, probe.height);
    ctx.drawImage(source as CanvasImageSource, 0, 0, w, h, 0, 0, probe.width, probe.height);
    let data: Uint8ClampedArray;
    try {
        data = ctx.getImageData(0, 0, probe.width, probe.height).data;
    } catch {
        // Tainted canvas should be impossible for local files, but never let the
        // probe break opening the image.
        return false;
    }
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) { return true; }
    }
    return false;
}

/** Promise wrapper over `canvas.toBlob`. Rejects instead of handing back null. */
export function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
        try {
            canvas.toBlob(
                (blob) => {
                    if (blob) { resolve(blob); } else { reject(new Error(`The browser could not write a ${mime} file.`)); }
                },
                mime,
                quality,
            );
        } catch (err) {
            reject(err);
        }
    });
}

export function canvasImageData(canvas: HTMLCanvasElement): ImageData {
    const ctx = canvas.getContext('2d', { willReadFrequently: true } as any) as CanvasRenderingContext2D;
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/** Wrap any throw from a third-party codec in a message a user can act on. */
export function codecError(message: string, cause: unknown): Error {
    const error = new Error(message);
    (error as any).cause = cause;
    return error;
}
